import moment from "moment";

export interface ParsedDateResult {
  isDate: boolean;
  date?: string; // MM/DD/YYYY format
  remainingText?: string;
}

/**
 * Detects and parses date from input text
 * Supports various formats:
 * - MM/DD/YYYY, M/D/YYYY, MM/DD/YY, M/D/YY
 * - DD/MM/YYYY, D/M/YYYY, DD/MM/YY, D/M/YY
 * - MM-DD-YYYY, M-D-YYYY, MM-DD-YY, M-D-YY
 * - DD-MM-YYYY, D-M-YYYY, DD-MM-YY, D-M-YY
 * 
 * Always returns date in MM/DD/YYYY format
 */
export function parseDateFromInput(input: string): ParsedDateResult {
  if (!input || typeof input !== "string") {
    return { isDate: false };
  }

  const trimmedInput = input.trim();
  
  // Pattern to match date: M/D/YYYY, MM/DD/YYYY, M/D/YY, MM/DD/YY
  // Also handles - and . as separators
  const datePattern = /(\d{1,2})[-\/\.](\d{1,2})[-\/\.](\d{2,4})/;
  const match = trimmedInput.match(datePattern);
  
  if (!match) {
    return { isDate: false };
  }
  
  const datePart = match[0];
  const first = parseInt(match[1], 10);
  const second = parseInt(match[2], 10);
  let year = parseInt(match[3], 10);
  
  // Handle 2-digit years: assume 2000s if < 50, 1900s if >= 50
  if (match[3].length === 2) {
    year = year < 50 ? 2000 + year : 1900 + year;
  }
  
  // Determine format by checking which number is > 12
  let month: number, day: number;
  
  if (first > 12 && second >= 1 && second <= 12) {
    // DD/MM format (first is day > 12, second is month)
    day = first;
    month = second;
  } else if (first >= 1 && first <= 12 && second > 12) {
    // MM/DD format (first is month, second is day > 12)
    month = first;
    day = second;
  } else if (first >= 1 && first <= 12 && second >= 1 && second <= 12) {
    // Ambiguous: both <= 12, try both formats
    // Try MM/DD format first (US format is more common)
    let dateMMDD = moment(`${year}-${String(first).padStart(2, "0")}-${String(second).padStart(2, "0")}`, "YYYY-MM-DD", true);
    let dateDDMM = moment(`${year}-${String(second).padStart(2, "0")}-${String(first).padStart(2, "0")}`, "YYYY-MM-DD", true);
    
    // Prefer MM/DD if both are valid, otherwise use whichever is valid
    if (dateMMDD.isValid()) {
      month = first;
      day = second;
    } else if (dateDDMM.isValid()) {
      month = second;
      day = first;
    } else {
      // Neither is valid, default to MM/DD format
      month = first;
      day = second;
    }
  } else {
    return { isDate: false };
  }
  
  // Validate month and day ranges
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return { isDate: false };
  }
  
  // Create and validate the date (strict parsing)
  const date = moment(`${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`, "YYYY-MM-DD", true);
  if (!date.isValid()) {
    return { isDate: false };
  }
  
  // Check if the entire input is just the date
  const normalizedInput = trimmedInput.replace(/[-\/\.]/g, "/");
  const normalizedDatePart = datePart.replace(/[-\/\.]/g, "/");
  
  if (normalizedInput === normalizedDatePart) {
    // Entire input is a date
    return {
      isDate: true,
      date: date.format("MM/DD/YYYY"),
      remainingText: "",
    };
  } else {
    // Date is part of the input, extract remaining text
    const remainingText = trimmedInput.replace(datePart, "").trim();
    return {
      isDate: true,
      date: date.format("MM/DD/YYYY"),
      remainingText: remainingText || undefined,
    };
  }
}

/**
 * Converts MM/DD/YYYY format to MM-DD-YYYY format (for API compatibility)
 */
export function convertToApiFormat(dateStr: string): string {
  if (!dateStr) return "";
  return dateStr.replace(/\//g, "-");
}

